/**
 * WP-V2-TW / WAVE5-W4 — Misia (maltanka, różowa kokardka) i Lola (corgi, niebieska bandamka).
 * Spacer wg harmonogramu czasu sesji; przy podejściu gracza — wave.
 * Tap → polska kwestia w dymku (rotacja) + lektor; dymek ~2.5 s, nie blokuje ruchu.
 */
import Phaser from 'phaser';
import { AnimatedDogAvatar } from '../../avatar/AnimatedDogAvatar.js';
import { emptyOutfitSlots } from '../../avatar/parseOutfit.js';
import { narrator } from '../../core/Narrator.js';
import { playFx } from '../../interact/fx.js';
import {
  LOLA_WAYPOINTS,
  MISIA_WAYPOINTS,
  sampleNpcSchedule,
} from './npcSchedule.js';

/** Miłe, dziecięce kwestie PL (min. 3) — rotacja na tap. */
const LINES_PL: Record<'misia' | 'lola', readonly string[]> = {
  misia: [
    'Hau! Cześć! Jak się masz?',
    'Chodź pobawić się ze mną!',
    'Mmm, pachniesz super!',
    'Lola jest moją najlepszą przyjaciółką!',
  ],
  lola: [
    'Hau hau! Fajnie Cię widzieć!',
    'Biegajmy razem po mieście!',
    'Lubię smakołyki i przytulasy!',
    'Misia i ja dbamy o Pieskowo!',
  ],
};

const BUBBLE_MS = 2500;

type FriendNpc = {
  id: 'misia' | 'lola';
  namePl: string;
  avatar: AnimatedDogAvatar;
  label: Phaser.GameObjects.Text;
  lastX: number;
  wavedUntil: number;
  lineIdx: number;
  bubble: Phaser.GameObjects.Text | null;
  bubbleTimer: Phaser.Time.TimerEvent | null;
};

const WAVE_RANGE = 110;
const WAVE_COOLDOWN = 4;

/** Givers offered by Misia/Lola tap (W9-B). Town kotek is separate. */
export const MISIA_LOLA_OFFER_GIVERS = ['misia', 'lola'] as const;

/** Map friend id → QuestLayer.offerGiver argument. */
export function offerGiverIdForFriend(id: 'misia' | 'lola'): 'misia' | 'lola' {
  return id;
}

type QuestLayerOfferApi = {
  offerGiver?: (giver: 'misia' | 'lola' | 'kotek') => void;
  setOfferScreenPos?: (x: number, y: number) => void;
};

export class MisiaLola {
  private friends: FriendNpc[] = [];
  private sessionT = 0;

  constructor(private readonly scene: Phaser.Scene) {}

  spawn(): void {
    this.friends.push(
      this.makeFriend('misia', 'Misia', 'maltese', {
        ...emptyOutfitSlots(),
        bow: 'bow_pink_big',
      }),
      this.makeFriend('lola', 'Lola', 'corgi', {
        ...emptyOutfitSlots(),
        collar: 'bandana_blue',
      }),
    );
    for (const f of this.friends) this.attachTapHandler(f);
  }

  /** Stuknięcie w Misię/Lolę → dymek z kwestią + machają + serduszka + oferta misji. */
  private attachTapHandler(f: FriendNpc): void {
    const hit = this.scene.add
      .circle(0, 0, 36, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Circle(0, 0, 36) });
    f.avatar.container.add(hit);
    hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
      this.onGreet(f);
    });
  }

  private onGreet(f: FriendNpc): void {
    const lines = LINES_PL[f.id];
    const line = lines[f.lineIdx % lines.length]!;
    f.lineIdx = (f.lineIdx + 1) % lines.length;

    this.showBubble(f, line);
    narrator.sayText(line, { immediate: true });

    playFx(this.scene, 'hearts', f.avatar.container.x, f.avatar.container.y - 30);
    if (this.sessionT >= f.wavedUntil) {
      f.wavedUntil = this.sessionT + WAVE_COOLDOWN;
      void f.avatar.playAction('wave');
    }

    // W9-B: po powitaniu zaproponuj misję od Misi/Loli (QuestLayer.offerGiver).
    this.offerQuestFromFriend(f);
  }

  private offerQuestFromFriend(f: FriendNpc): void {
    const ql = this.scene.game.scene.getScene('QuestLayer') as QuestLayerOfferApi | null;
    if (!ql || typeof ql.offerGiver !== 'function') return;
    const x = f.avatar.container.x;
    const y = f.avatar.container.y - 40;
    if (typeof ql.setOfferScreenPos === 'function') {
      ql.setOfferScreenPos(x, y);
    }
    ql.offerGiver(offerGiverIdForFriend(f.id));
  }

  private showBubble(f: FriendNpc, text: string): void {
    f.bubbleTimer?.remove(false);
    f.bubbleTimer = null;
    f.bubble?.destroy();
    f.bubble = null;

    const bubble = this.scene.add
      .text(0, -56, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
        align: 'center',
        wordWrap: { width: 160 },
      })
      .setOrigin(0.5)
      .setDepth(5);
    // dymek nie przechwytuje klików — ruch gracza / inne tapy działają
    bubble.disableInteractive();
    f.avatar.container.add(bubble);
    f.bubble = bubble;

    f.bubbleTimer = this.scene.time.delayedCall(BUBBLE_MS, () => {
      if (f.bubble === bubble) {
        bubble.destroy();
        f.bubble = null;
      }
      f.bubbleTimer = null;
    });
  }

  private makeFriend(
    id: 'misia' | 'lola',
    namePl: string,
    breed: string,
    slots: ReturnType<typeof emptyOutfitSlots>,
  ): FriendNpc {
    const start = id === 'misia' ? MISIA_WAYPOINTS[0]! : LOLA_WAYPOINTS[0]!;
    const avatar = new AnimatedDogAvatar({
      scene: this.scene,
      x: start.x,
      y: start.y,
      breed,
      scale: 0.85,
      slots,
      depth: 90,
    });
    const label = this.scene.add
      .text(0, 48, namePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: id === 'misia' ? '#ffd6e8cc' : '#d6e8ffcc',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5);
    avatar.container.add(label);
    return {
      id,
      namePl,
      avatar,
      label,
      lastX: start.x,
      wavedUntil: 0,
      lineIdx: 0,
      bubble: null,
      bubbleTimer: null,
    };
  }

  update(dtSec: number, playerPos: { x: number; y: number } | null): void {
    this.sessionT += dtSec;

    for (const f of this.friends) {
      const wps = f.id === 'misia' ? MISIA_WAYPOINTS : LOLA_WAYPOINTS;
      const phase = f.id === 'lola' ? 9 : 0;
      const sample = sampleNpcSchedule(this.sessionT, wps, { phaseOffsetSec: phase });

      const prevX = f.avatar.container.x;
      const prevY = f.avatar.container.y;
      const dx = sample.x - prevX;
      const dy = sample.y - prevY;
      const dist = Math.hypot(dx, dy);

      if (sample.traveling && dist > 1.5) {
        // velocity dla animacji chodu; pozycję korygujemy do harmonogramu
        const sp = Math.min(160, dist / Math.max(dtSec, 1e-3));
        f.avatar.setVelocity((dx / dist) * sp, (dy / dist) * sp);
      } else {
        f.avatar.setVelocity(0, 0);
        f.avatar.stop();
      }

      f.avatar.update(dtSec);
      f.avatar.container.setPosition(sample.x, sample.y);
      f.lastX = sample.x;

      // podejście gracza → machają
      if (playerPos) {
        const d = Math.hypot(sample.x - playerPos.x, sample.y - playerPos.y);
        if (d < WAVE_RANGE && this.sessionT >= f.wavedUntil) {
          f.wavedUntil = this.sessionT + WAVE_COOLDOWN;
          void f.avatar.playAction('wave');
        }
      }
    }
  }

  /** Pozycje do giveTargets / pet (testy). */
  getPositions(): Array<{ id: string; x: number; y: number }> {
    return this.friends.map((f) => ({
      id: f.id,
      x: f.avatar.container.x,
      y: f.avatar.container.y,
    }));
  }

  destroy(): void {
    for (const f of this.friends) {
      f.bubbleTimer?.remove(false);
      f.bubble?.destroy();
      f.avatar.destroy();
    }
    this.friends = [];
  }
}
